'use client'

import React from 'react'
import { motion, Variants } from 'framer-motion'
import classes from './index.module.css'
import { useTranslations } from 'next-intl'
import {
  ShippingIcon,
  GuaranteeIcon,
  SupportIcon,
  PaymentIcon,
} from './TrustIcons'

/**
 * AnimatedFeatures — Premium Trust Band
 *
 * Uses bespoke SVG icons (TrustIcons.tsx) instead of Lucide.
 * Layout: 4-column horizontal band, icon-left / text-right.
 *
 * NOTE: Update 'payment.description' in de.json / en.json to positive framing:
 * "Sichere Banküberweisung — diskret & zuverlässig."
 * Avoid phrasing payment as a limitation (no 'nur' / 'only').
 */

const containerVariants: Variants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.1, delayChildren: 0.05 },
  },
}

const itemVariants: Variants = {
  hidden: { y: 16, opacity: 0 },
  visible: {
    y: 0,
    opacity: 1,
    transition: { duration: 0.45, ease: 'easeOut' },
  },
}

const AnimatedFeatures: React.FC = () => {
  const t = useTranslations('features')
  const [activeIndex, setActiveIndex] = React.useState(0)
  const [isHovered, setIsHovered] = React.useState(false)

  const features = [
    {
      key: 'shipping',
      title: t('shipping.title'),
      description: t('shipping.description'),
      Icon: ShippingIcon,
    },
    {
      key: 'quality',
      title: t('quality.title'),
      description: t('quality.description'),
      Icon: GuaranteeIcon,
    },
    {
      key: 'support',
      title: t('support.title'),
      description: t('support.description'),
      Icon: SupportIcon,
    },
    {
      key: 'payment',
      title: t('payment.title'),
      description: t('payment.description'),
      Icon: PaymentIcon,
    },
  ]

  React.useEffect(() => {
    if (isHovered) return
    const timer = setInterval(() => {
      setActiveIndex((prev) => (prev + 1) % features.length)
    }, 5000)
    return () => clearInterval(timer)
  }, [isHovered, features.length])

  return (
    <motion.section
      data-testid="animated-features"
      className={`${classes.trustBand} backdrop-blur-2xl`}
      variants={containerVariants}
      initial="hidden"
      whileInView="visible"
      viewport={{ once: true, margin: '-80px' }}
    >
      <div className={classes.grid}>
        {features.map((feature, index) => {
          const isActive = index === activeIndex

          return (
            <motion.div
              key={feature.key}
              data-testid="feature-item"
              className={`${classes.item} ${isActive ? classes.active : ''}`}
              variants={itemVariants}
              onMouseEnter={() => {
                setActiveIndex(index)
                setIsHovered(true)
              }}
              onMouseLeave={() => setIsHovered(false)}
            >
              {index > 0 && <span className={classes.divider} aria-hidden="true" />}

              <div className={classes.iconWrap} aria-hidden="true">
                <feature.Icon className={classes.icon} size={22} />
              </div>

              <div className={classes.text}>
                <h3 className={classes.title}>{feature.title}</h3>
                <div className={classes.descriptionWrapper}>
                  <p className={classes.description}>{feature.description}</p>
                </div>
              </div>
              
              <div className={classes.progressBar} />
            </motion.div>
          )
        })}
      </div>
    </motion.section>
  )
}

export default AnimatedFeatures

